import { useEffect, useState } from 'react';
import axios from 'axios';
import { Sparkles, Plus, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useCart } from '@/context/CartContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

// Resolve an image URL — passes through absolute + /api/uploads URLs.
function resolveImg(url) {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api/')) return `${BACKEND}${url}`;
  return url;
}

// Soft polished cabochon gradients — used as a fallback for pocket stones
// when a proper photo is missing.
const STONE_FALLBACK = {
  'rose-quartz':
    'radial-gradient(circle at 30% 30%, #FCEAEE 0%, #F2B8C2 35%, #D78A9A 70%, #A45D6F 100%)',
  amethyst:
    'radial-gradient(circle at 30% 30%, #EBE0F2 0%, #B89BD4 40%, #7C5DA8 75%, #4F3B73 100%)',
  citrine:
    'radial-gradient(circle at 30% 30%, #FFF4D6 0%, #F5D78A 40%, #D9A847 75%, #9C7322 100%)',
  moonstone:
    'radial-gradient(circle at 30% 30%, #FBFAF5 0%, #EAEAE2 40%, #C9D0C8 75%, #8C9C9E 100%)',
};

// Legacy hardcoded fallback — used only when a bracelet's own
// `addon_product_ids` / `pocket_stone_ids` fields are missing (very fresh
// DBs / newly-imported products). The admin's product-edit dialog is the
// source of truth.
const LEGACY_PAIRINGS = {
  'two-become-one-bracelet': ['rose-quartz', 'moonstone'],
  'the-two-become-one-bracelet': ['rose-quartz', 'moonstone'],
  'soul-sister-bracelet': ['rose-quartz', 'amethyst'],
  'the-soul-sister-bracelet': ['rose-quartz', 'amethyst'],
  'mother-of-the-bride-bracelet': ['moonstone', 'rose-quartz'],
  'the-mother-of-the-bride-bracelet': ['moonstone', 'rose-quartz'],
  'flower-girl-bracelet': ['moonstone', 'citrine'],
  'the-flower-girl-bracelet': ['moonstone', 'citrine'],
  'akamas-sunset-bracelet': ['citrine', 'moonstone'],
  'the-akamas-sunset-bracelet': ['citrine', 'moonstone'],
  'dreams-come-true-bracelet': ['citrine', 'amethyst'],
  'the-dreams-come-true-bracelet': ['citrine', 'amethyst'],
  'be-still-bracelet': ['amethyst', 'moonstone'],
  'the-be-still-bracelet': ['amethyst', 'moonstone'],
  'i-am-enough-bracelet': ['amethyst', 'rose-quartz'],
  'the-i-am-enough-bracelet': ['amethyst', 'rose-quartz'],
  'let-them-bracelet': ['amethyst', 'citrine'],
  'the-let-them-bracelet': ['amethyst', 'citrine'],
  'lest-we-forget-bracelet': ['moonstone', 'amethyst'],
  'the-lest-we-forget-bracelet': ['moonstone', 'amethyst'],
};

function colorKeyFor(product) {
  return (product.bead_color || product.id.replace('pocket-stone-', '')).toLowerCase();
}

export default function PocketStoneUpsell({ productId }) {
  const { addToCart, loading: cartLoading } = useCart();
  const [addons, setAddons] = useState([]);
  const [adding, setAdding] = useState(null);
  const [added, setAdded] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [{ data: product }, { data: allProducts }] = await Promise.all([
          axios.get(`${API}/products/${productId}`),
          axios.get(`${API}/products`),
        ]);
        if (cancelled) return;

        // Preferred: admin-curated list on the product. Read the new field
        // name first, fall back to the legacy one for old data.
        const configuredIds = Array.isArray(product?.addon_product_ids) && product.addon_product_ids.length
          ? product.addon_product_ids
          : (Array.isArray(product?.pocket_stone_ids) ? product.pocket_stone_ids : []);

        let chosen = [];
        if (configuredIds.length) {
          chosen = configuredIds
            .map((id) => allProducts.find((p) => p.id === id))
            .filter(Boolean);
        }

        // Fallback: legacy hardcoded pairings (very old DBs only).
        if (chosen.length === 0) {
          const stonesCatalogue = (allProducts || []).filter(
            (p) => p.category === 'pocket-stone',
          );
          const legacyKeys = LEGACY_PAIRINGS[productId] || [];
          chosen = legacyKeys
            .map((key) => stonesCatalogue.find((p) => colorKeyFor(p) === key))
            .filter(Boolean);
        }

        setAddons(chosen);
      } catch {
        if (!cancelled) setAddons([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [productId]);

  const handleAdd = async (addon) => {
    if (adding) return;
    setAdding(addon.id);
    try {
      await addToCart(addon.id, 1);
      setAdded((prev) => ({ ...prev, [addon.id]: true }));
      toast.success(`${addon.name} added to your cart`);
      setTimeout(() => {
        setAdded((prev) => ({ ...prev, [addon.id]: false }));
      }, 2200);
    } catch {
      toast.error('Could not add — please try again');
    } finally {
      setAdding(null);
    }
  };

  if (addons.length === 0) return null;

  return (
    <section
      className="mt-10 p-7 sm:p-8 bg-gradient-to-br from-[#F5F5F0] to-[#FAF9F6] border border-[#CAD2C5]/40 rounded-sm"
      data-testid="pocket-stone-upsell"
    >
      <header className="text-center mb-6">
        <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#84A98C] mb-3">
          <Sparkles className="w-3 h-3 text-[#D4AF37]" strokeWidth={1.5} />
          <span>A quiet companion</span>
          <Sparkles className="w-3 h-3 text-[#D4AF37]" strokeWidth={1.5} />
        </div>
        <h3 className="font-serif text-2xl sm:text-[1.6rem] text-[#354F52] mb-2 leading-snug">
          Complete the intention
        </h3>
        <div className="w-12 h-px bg-[#D4AF37] mx-auto mb-3" />
        <p className="font-serif italic text-[#52796F] text-sm sm:text-base max-w-md mx-auto leading-relaxed">
          Pair this piece with a matching companion, hand-picked by Lindy
          from the studio in Paphos.
        </p>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        {addons.map((addon) => {
          const isStone = addon.category === 'pocket-stone';
          const stoneKey = isStone ? colorKeyFor(addon) : null;
          const gradient = stoneKey ? STONE_FALLBACK[stoneKey] : null;
          const isAdded = !!added[addon.id];
          const isAdding = adding === addon.id;
          const testKey = stoneKey || addon.id;
          return (
            <div
              key={addon.id}
              className="flex gap-4 items-start p-4 bg-white/60 rounded-sm border border-[#CAD2C5]/30 hover:border-[#D4AF37]/40 transition-colors"
              data-testid={`pocket-stone-${testKey}`}
            >
              <div className="shrink-0 relative" style={{ zIndex: 0 }}>
                {addon.image ? (
                  <div
                    className="w-16 h-16 rounded-sm overflow-visible relative transition-all duration-300 ease-out hover:z-20 hover:scale-[3] hover:rounded-lg hover:shadow-2xl cursor-zoom-in"
                    style={{ transformOrigin: 'center left' }}
                  >
                    <img
                      src={resolveImg(addon.image)}
                      alt={addon.name}
                      className="w-full h-full object-cover rounded-sm shadow-inner"
                      loading="lazy"
                    />
                  </div>
                ) : (
                  <div
                    className="w-16 h-16 rounded-full shadow-inner overflow-hidden relative transition-transform duration-300 ease-out hover:scale-[2] cursor-zoom-in"
                    style={{
                      background:
                        gradient ||
                        'radial-gradient(circle at 30% 30%, #F5F5F0 0%, #CAD2C5 60%, #84A98C 100%)',
                      boxShadow:
                        'inset -4px -6px 12px rgba(0,0,0,0.18), inset 3px 4px 8px rgba(255,255,255,0.55), 0 2px 6px rgba(0,0,0,0.08)',
                    }}
                    aria-hidden="true"
                  />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-serif text-[#354F52] text-lg leading-tight">
                  {addon.name.replace(' Pocket Stone', '')}
                </p>
                {addon.inspiration && (
                  <p className="text-[10px] tracking-widest uppercase text-[#D4AF37] mt-1 mb-1">
                    {addon.inspiration.split(':')[1]?.trim() || addon.inspiration}
                  </p>
                )}
                <p className="text-sm text-[#52796F] leading-relaxed mb-3">
                  {addon.product_description || addon.description?.split('\n')[0]}
                </p>
                <div className="flex items-center justify-between gap-2">
                  <p className="font-serif text-[#354F52] text-base">
                    €{(addon.price || 0).toFixed(2)}
                  </p>
                  <button
                    type="button"
                    onClick={() => handleAdd(addon)}
                    disabled={isAdding || cartLoading}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] uppercase tracking-widest rounded-sm transition-colors ${
                      isAdded
                        ? 'bg-[#84A98C] text-[#FAF9F6]'
                        : 'bg-[#354F52] text-[#FAF9F6] hover:bg-[#52796F] disabled:opacity-60'
                    }`}
                    data-testid={`add-pocket-stone-${testKey}`}
                  >
                    {isAdded ? (
                      <>
                        <Check className="w-3 h-3" strokeWidth={2} />
                        Added
                      </>
                    ) : (
                      <>
                        <Plus className="w-3 h-3" strokeWidth={2} />
                        {isAdding ? 'Adding…' : `Add ${isStone ? 'stone' : 'to cart'}`}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <footer className="mt-6 text-center">
        <div className="flex items-center justify-center gap-2">
          <img
            src="/images/lindy-in-studio.jpg"
            alt="Lindy"
            className="w-8 h-8 rounded-full object-cover ring-1 ring-[#D4AF37]/40"
            style={{ objectPosition: '50% 30%' }}
          />
          <p className="text-xs text-[#52796F] italic">
            Hand-picked by Lindy &middot; ships in the same parcel
          </p>
        </div>
      </footer>
    </section>
  );
}
